/**
 * Carrying a turn's shape through the database, not just its words.
 *
 * Live, a tool-using turn renders a chain-of-thought rail — "Working — step 3",
 * a node per step labelled by what that step did, tool badges beneath each —
 * and its reasoning in a disclosure above the answer. Rebuilt from
 * `chat_messages`, a turn came back as a single text part: no reasoning, no
 * rail, no badges. Measured on the rig 2026-09-15 against a turn that made 8
 * tool calls across 6 reasoning steps and persisted `message_metadata` keys
 * `chat_attempt_id, model, provider, server_persisted, tool_stats, usage`.
 *
 * The rail is DERIVED, not stored: `groupPartsIntoSteps` splits `parts` on
 * `step-start` and reads tool names off `tool-*` part types. So restoring the
 * rail means restoring those parts, which means persisting the per-step
 * structure they encode. That is what `turn_steps` is.
 *
 * Two things are deliberately NOT persisted:
 *
 *  - Per-step TEXT. `/mainchat` renders the body from
 *    `groupPartsIntoSteps(parts).allText`, and the saved `content` is already
 *    every step's text joined (`collectAssistantText`). Restoring step text
 *    would print the answer twice. Rail labels fall back to
 *    `describeStepTools`, the same path a step that wrote no prose already
 *    takes.
 *  - Tool INPUTS and OUTPUTS. Only the name is kept, which is all the rail
 *    needs, and it keeps tool payloads out of a column sized for metadata.
 */

import { groupPartsIntoSteps, STEP_START_PART_TYPE } from '@/lib/ai/message-steps'

/** Minimal shape needed from an SDK step — avoids importing the generics. */
interface ReasoningBearingStep {
  reasoningText?: string | null
  reasoning?: ReadonlyArray<{ text?: string | null } | null | undefined> | null
  toolCalls?: ReadonlyArray<{ toolName?: string } | null | undefined> | null
}

/**
 * One step of a turn, as persisted. Both fields are optional and omitted when
 * empty: a step that only called tools has no reasoning, a step that only
 * thought has no tools, and `{}` holds a place in the sequence for a step that
 * did neither — position is load-bearing, because an entry dropped from the
 * middle shifts every later step onto the wrong rail node.
 */
export interface PersistedTurnStep {
  reasoning?: string
  tools?: string[]
}

/**
 * A part rebuilt for a reloaded turn.
 *
 * Tool parts are restored in their SETTLED state rather than as a bare
 * `{type:'tool-x'}` marker: the SDK's `UIMessagePart` union has no member for
 * the bare form, and casting around the union is not worth a rail. So the part
 * is legal — deterministic `toolCallId`, `output-available` — and carries no
 * output, because only the tool's NAME is persisted. The row therefore says
 * what ran and claims nothing about what it returned.
 */
export type RestoredPart =
  | { type: typeof STEP_START_PART_TYPE }
  | { type: 'reasoning'; text: string }
  | {
      type: `tool-${string}`
      toolCallId: string
      state: 'output-available'
      input: undefined
      output: string
    }

/**
 * How much reasoning one turn may put into `message_metadata`.
 *
 * It is one `text` column on a clinical database and reasoning is unbounded,
 * so persisting it whole is how a chat table quietly becomes a transcript
 * store nobody sized for. The cap is per TURN, not per step.
 */
export const MAX_PERSISTED_REASONING_CHARS = 12_000

/** Marks a truncation so a clipped block never reads as a complete thought. */
export const REASONING_TRUNCATION_NOTE = '… [reasoning truncated]'

/** Prefix `groupPartsIntoSteps` reads tool names from. */
const TOOL_PREFIX = 'tool-'

/** Marks a tool call id as rebuilt rather than one the provider issued. */
const RESTORED_CALL_PREFIX = 'restored-'

/**
 * The turn's steps, from the SDK's `steps[]`.
 *
 * Reads every step rather than `onFinish`'s own fields, which describe the
 * FINAL step alone — the same trap `collectAssistantText` documents for text.
 */
export function collectTurnSteps(
  steps: ReadonlyArray<ReasoningBearingStep | null | undefined> | null | undefined,
): PersistedTurnStep[] {
  if (!Array.isArray(steps)) return []
  return withBudget(
    steps.map((step) => ({
      reasoning: reasoningTextOf(step),
      tools: toolNamesOf(step?.toolCalls),
    })),
  )
}

/**
 * The same steps, read back out of the flattened UIMessage the browser holds.
 *
 * It exists because the client's save lands AFTER the server's and overwrites
 * the row — the hazard `assistant-text.ts` documents for the empty-turn notice.
 * Were only the server writing this, the client would wipe it on every turn
 * that finished in an open tab, which is nearly all of them. The client never
 * sees `steps[]`, so it recovers the boundaries from the parts, exactly as
 * `groupPartsIntoSteps` does.
 */
export function turnStepsFromParts(parts: unknown): PersistedTurnStep[] {
  if (!Array.isArray(parts)) return []
  // Delegated, not re-split. `step-rail-wiring` asserts message-steps is the
  // only place boundaries are interpreted, and a second interpretation is
  // precisely what produced the run-on paragraph it was written to prevent.
  const { steps, finalStep } = groupPartsIntoSteps(parts)
  const all = [...steps, finalStep]
  // Nothing to say about a turn that neither reasoned nor called a tool —
  // persisting a row of empty steps for it would restore boundaries that carry
  // no information. But once ANY step has content the whole sequence is kept,
  // empties included: dropping a middle one shifts every later step onto the
  // wrong rail node.
  if (!all.some((step) => step.reasoning || step.tools.length > 0)) return []
  return withBudget(all.map((step) => ({ reasoning: step.reasoning, tools: step.tools })))
}

/**
 * The parts to put in FRONT of a reloaded turn's text.
 *
 * The caller appends the text part, which lands in the final bucket — so N
 * persisted steps produce N boundaries and `groupPartsIntoSteps` pops the last
 * into `finalText`, leaving N-1 rail nodes. That is the same count a settled
 * live turn shows, because there too the closing step is the answer.
 *
 * A turn saved under the earlier flat `reasoning: string[]` shape restores as
 * reasoning parts with NO boundaries, which is exactly how it behaved before:
 * `groupPartsIntoSteps` treats a message with no boundaries as a single answer
 * and manufactures no rail.
 */
export function restoredTurnParts(metadata: unknown): RestoredPart[] {
  if (!metadata || typeof metadata !== 'object') return []
  const meta = metadata as { turn_steps?: unknown; reasoning?: unknown }

  if (Array.isArray(meta.turn_steps)) {
    const parts: RestoredPart[] = []
    let stepIndex = -1
    for (const raw of meta.turn_steps) {
      stepIndex += 1
      // The boundary is emitted for EVERY entry, junk included. Position is
      // load-bearing: skipping an unreadable entry shifts every later step
      // onto the wrong rail node, which is a worse lie than a bare node
      // saying only that a step happened.
      parts.push({ type: STEP_START_PART_TYPE })
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
      const step = raw as PersistedTurnStep
      const reasoning = typeof step.reasoning === 'string' ? step.reasoning.trim() : ''
      if (reasoning) parts.push({ type: 'reasoning', text: reasoning })
      if (!Array.isArray(step.tools)) continue
      for (const name of step.tools) {
        if (typeof name !== 'string' || !name.trim()) continue
        parts.push({
          type: `${TOOL_PREFIX}${name.trim()}`,
          // Deterministic, so it is stable across re-renders. The original
          // call id is not persisted and inventing a random one would churn
          // React keys on every render of a settled turn.
          toolCallId: `${RESTORED_CALL_PREFIX}${stepIndex}-${parts.length}`,
          state: 'output-available',
          input: undefined,
          output: '',
        })
      }
    }
    return parts
  }

  // Legacy flat shape.
  if (!Array.isArray(meta.reasoning)) return []
  const parts: RestoredPart[] = []
  for (const entry of meta.reasoning) {
    if (typeof entry !== 'string') continue
    const text = entry.trim()
    if (text) parts.push({ type: 'reasoning', text })
  }
  return parts
}

/** A step's reasoning, preferring the SDK's joined field over the raw parts. */
function reasoningTextOf(step: ReasoningBearingStep | null | undefined): string {
  if (!step || typeof step !== 'object') return ''

  const joined = step.reasoningText
  if (typeof joined === 'string' && joined.trim()) return joined.trim()

  if (!Array.isArray(step.reasoning)) return ''
  return step.reasoning
    .map((part) => (typeof part?.text === 'string' ? part.text : ''))
    .join('')
    .trim()
}

/** Tool names called in a step, in call order, duplicates kept. */
function toolNamesOf(
  toolCalls: ReadonlyArray<{ toolName?: string } | null | undefined> | null | undefined,
): string[] {
  if (!Array.isArray(toolCalls)) return []
  return toolCalls
    .map((call) => call?.toolName)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

/**
 * Trim to the per-turn allowance and drop empty fields.
 *
 * The budget is divided evenly across the steps that actually reasoned, rather
 * than spent in order: spending it first-come lets one runaway early step
 * consume the whole allowance and push out the final step's conclusion, which
 * is usually the part worth reading.
 */
function withBudget(
  raw: Array<{ reasoning: string; tools: string[] }>,
): PersistedTurnStep[] {
  const reasoningCount = raw.filter((step) => step.reasoning).length
  const perStep = reasoningCount
    ? Math.floor(MAX_PERSISTED_REASONING_CHARS / reasoningCount)
    : 0

  return raw.map(({ reasoning, tools }) => {
    const step: PersistedTurnStep = {}
    if (reasoning) {
      step.reasoning =
        reasoning.length <= perStep
          ? reasoning
          : reasoning.slice(0, perStep) + REASONING_TRUNCATION_NOTE
    }
    if (tools.length > 0) step.tools = tools
    return step
  })
}
